import { Calendar, ClipboardList, Clock, List, type LucideIcon } from 'lucide-react';

import { t } from '../../../../lib/i18n';
import { cn } from '../../utils';
import type { ScheduleScreen } from './session-schedule-types';

/**
 * The top-level admin switcher: the three Design D session views plus, as a
 * fourth tab, the legacy per-lot board (`../legacy-board.tsx`) — see brief
 * point 3. Styled after the web `ViewToggle`
 * (`apps/auction/.../components/ViewToggle.tsx`) and the admin's own previous
 * `view-toggle.tsx` (kept, unchanged, for the legacy board's own internal
 * calendar/list/timeline switch).
 *
 * NOT `role="tablist"`/`role="tab"`. The views it switches render as plain
 * `<section>`s, not `role="tabpanel"`, and there is no `aria-controls` or
 * roving-tabindex arrow-key handling — so the tab roles announced "tab 1 of 4"
 * against panels that do not exist and promised a keyboard model that was not
 * implemented. A half-built ARIA pattern is worse for a screen-reader user
 * than none, so this is a labelled group of `aria-pressed` toggle buttons,
 * matching `./session-filter-bar.tsx`. Restore the tabs pattern only together
 * with the panels and the arrow keys.
 */

interface SessionViewToggleProps {
  currentScreen: ScheduleScreen;
  onScreenChange: (_screen: ScheduleScreen) => void;
  className?: string;
}

const SCREENS: readonly ScheduleScreen[] = ['calendar', 'list', 'timeline', 'board'];

const ICON: Record<ScheduleScreen, LucideIcon> = {
  calendar: Calendar,
  list: List,
  timeline: Clock,
  board: ClipboardList,
};

const LABEL_KEY: Record<ScheduleScreen, string> = {
  calendar: 'auctionSchedule.views.calendar',
  list: 'auctionSchedule.views.list',
  timeline: 'auctionSchedule.views.timelineShort',
  board: 'auctionSchedule.board.tabLabel',
};

export function SessionViewToggle({ currentScreen, onScreenChange, className }: SessionViewToggleProps) {
  return (
    <div
      role="group"
      aria-label={t('auctionSchedule.views.switcherLabel')}
      className={cn('flex w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px]', className)}
    >
      {SCREENS.map((screen) => {
        const active = screen === currentScreen;
        const Icon = ICON[screen];

        return (
          <button
            key={screen}
            type="button"
            aria-pressed={active}
            onClick={() => onScreenChange(screen)}
            className={cn(
              'flex h-9 cursor-pointer items-center gap-2 rounded-cf-md px-3 text-[13px] font-semibold whitespace-nowrap',
              active ? 'bg-cf-brand text-white' : 'text-cf-ink hover:bg-cf-surface',
            )}
          >
            <Icon size={15} strokeWidth={1.75} />
            <span>{t(LABEL_KEY[screen])}</span>
          </button>
        );
      })}
    </div>
  );
}
